import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Circle, X, RotateCcw, MessageSquare } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { DateInput } from '../ui/date-input';
import { taxBasAPI, errText } from '../../lib/taxBasAPI';
import { DocUploader } from '../portal/DocUploader';

const fmt = (v) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '');
const STATUS = { open: 'Waiting for client', answered: 'Client responded', received: 'Received' };
const run = async (fn, ok, reload) => { try { await fn(); if (ok) toast.success(ok); reload(); } catch (e) { toast.error(errText(e)); } };

export function Checklist({ kind, refId, cl, staff, reload, onUpload, locked }) {
  const [label, setLabel] = useState('');
  if (!cl) return null;
  return (
    <Card data-testid={`checklist-${kind}`}>
      <CardHeader className="pb-2"><CardTitle className="flex items-center justify-between text-base">Document checklist
        <Badge variant={cl.outstanding ? 'secondary' : 'outline'} data-testid="checklist-outstanding">{cl.outstanding ? `${cl.outstanding} outstanding` : 'All received'}</Badge></CardTitle></CardHeader>
      <CardContent className="space-y-1 text-sm">
        {cl.items.map((i) => (
          <div key={i.key} className="flex flex-wrap items-center gap-2 border-b py-1 last:border-0" data-testid={`checklist-item-${i.key}`}>
            {i.satisfied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
            <span className="flex-1">{i.label}{!i.required && <span className="text-xs text-muted-foreground"> (optional)</span>}</span>
            {staff && <span className="text-xs text-muted-foreground">{i.source}{i.document_ids.length ? ` · ${i.document_ids.length} file(s)` : ''}</span>}
            {!staff && !i.satisfied && i.property_id && <span className="text-xs text-muted-foreground">Upload under “Your rental properties” above</span>}
            {!staff && !i.satisfied && !locked && onUpload && !i.property_id && <DocUploader testId={`checklist-upload-${i.key}`} upload={(f, desc) => onUpload(f, i, desc)} onDone={reload} />}
            {staff && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => run(() => taxBasAPI.clRemove(kind, refId, i.key), 'Removed', reload)} data-testid={`checklist-remove-${i.key}`}><X className="h-3 w-3" /></Button>}
          </div>
        ))}
        {staff && cl.removed.length > 0 && <p className="pt-1 text-xs text-muted-foreground">Removed: {cl.removed.map((k) => (
          <button key={k} type="button" className="mr-2 underline" onClick={() => run(() => taxBasAPI.clRestore(kind, refId, k), 'Restored', reload)} data-testid={`checklist-restore-${k}`}><RotateCcw className="mr-0.5 inline h-3 w-3" />{k}</button>))}</p>}
        {staff && (
          <div className="flex gap-2 pt-2"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Add an item for this client" className="h-8" data-testid="checklist-add-input" />
            <Button size="sm" disabled={label.trim().length < 2} onClick={() => run(() => taxBasAPI.clAdd(kind, refId, label.trim()).then(() => setLabel('')), 'Item added', reload)} data-testid="checklist-add">Add</Button></div>
        )}
      </CardContent>
    </Card>
  );
}

function NewRequest({ kind, refId, reload }) {
  const [text, setText] = useState('');
  const [due, setDue] = useState('');
  const [idem, setIdem] = useState(() => crypto.randomUUID());  // one key per compose: double-clicks don't duplicate, new requests always send
  const send = () => run(() => taxBasAPI.irCreate({ kind, ref_id: refId, text: text.trim(), due_date: due || null, idempotency_key: idem })
    .then(() => { setText(''); setDue(''); setIdem(crypto.randomUUID()); }), 'Request sent to the client', reload);
  return (
    <div className="space-y-2 rounded border p-2">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Please upload your June 2026 bank statement" data-testid="ir-text" />
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Due date (optional)<DateInput value={due} onChange={(v) => setDue(v || '')} className="h-8" data-testid="ir-due" /></label>
        <Button size="sm" disabled={text.trim().length < 3} onClick={send} data-testid="ir-send">Send request</Button>
      </div>
    </div>
  );
}

export function InfoRequests({ kind, refId, items, staff, reload, locked }) {
  const [reply, setReply] = useState({});
  const open = (items || []).filter((x) => x.status === 'open').length;
  return (
    <Card data-testid={`info-requests-${kind}`}>
      <CardHeader className="pb-2"><CardTitle className="flex items-center justify-between text-base">Information requests
        <Badge variant={open ? 'secondary' : 'outline'} data-testid="ir-open-count">{open} open</Badge></CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {staff && !locked && <NewRequest kind={kind} refId={refId} reload={reload} />}
        {(items || []).length === 0 && <p className="text-xs text-muted-foreground">No requests.</p>}
        {(items || []).map((x) => (
          <div key={x.id} className="space-y-1 rounded border p-2" data-testid={`ir-${x.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex-1 whitespace-pre-wrap">{x.text}</span>
              <Badge variant={x.overdue ? 'destructive' : 'outline'} data-testid={`ir-status-${x.id}`}>{x.overdue ? 'Overdue' : STATUS[x.status]}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{x.due_date && `Due ${fmt(x.due_date)} · `}{x.reminders.length} reminder(s) · <Link className="underline" to={x.thread_link}><MessageSquare className="inline h-3 w-3" /> conversation</Link></p>
            {staff && <ul className="text-xs text-muted-foreground">{x.history.map((h, n) => <li key={n}>{fmt(h.at)} {h.event}{h.via ? ` (${h.via})` : ''}</li>)}</ul>}
            {staff && x.status !== 'received' && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => run(() => taxBasAPI.irReceived(x.id), 'Marked received', reload)} data-testid={`ir-received-${x.id}`}>Mark received</Button>
                {x.status === 'open' && <Button size="sm" variant="ghost" onClick={() => run(() => taxBasAPI.irRemind(x.id), 'Reminder sent', reload)} data-testid={`ir-remind-${x.id}`}>Send reminder</Button>}
              </div>
            )}
            {!staff && x.status !== 'received' && (
              <div className="space-y-2">
                <div className="flex gap-2"><Input value={reply[x.id] || ''} onChange={(e) => setReply({ ...reply, [x.id]: e.target.value })} placeholder="Reply" className="h-8" data-testid={`ir-reply-input-${x.id}`} />
                  <Button size="sm" disabled={!(reply[x.id] || '').trim()} onClick={() => run(() => taxBasAPI.irReply(x.id, reply[x.id].trim()).then(() => setReply({ ...reply, [x.id]: '' })), 'Reply sent', reload)} data-testid={`ir-reply-${x.id}`}>Send</Button></div>
                <DocUploader testId={`ir-upload-${x.id}`} upload={(f, desc) => taxBasAPI.irUpload(x.id, f, { description: desc })} onDone={reload} />
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
